import BoardComponent from './Board.component';
import BoardEditor from './Board.editor';
import BoardProperties from './Board.properties';
import { adaptBoardProps, adaptBoardEditorProps } from './Board.adapter';
import type { BoardProps, BoardEditorProps, BoardData } from './Board.types';
import { registerComponent } from '../registry';
import ComponentSkinWrapper from '../../skins/ComponentSkinWrapper';
import { BoardSkinnable } from './BoardSkinnable';
import { COMPONENT_TYPES } from '../../../constants';

// 간편한 접근을 위한 상수 정의
export const COMPONENT_TYPE = 'Board';
export const COMPONENT_ICON = 'ViewList'; // Material UI 아이콘 이름
export const COMPONENT_LABEL = '게시판';
export const COMPONENT_DESCRIPTION = '게시판 목록, 게시글 보기, 작성, 수정 등을 제공하는 컴포넌트입니다.';

// 기본 속성 정의
export const defaultProps: BoardProps = {
  style: {
    paddingTop: '20px',
    paddingRight: '20px',
    paddingBottom: '20px',
    paddingLeft: '20px',
    backgroundColor: 'transparent',
    borderRadius: '0px',
    boxShadow: 'none',
    device: {
      pc: {},
      mobile: {}
    }
  },
  data: {
    title: '게시판',
    parameter: '',
    items: [],
    showNewButton: true,
    showDetailButtons: true,
    showEditButton: true,
    showDeleteButton: true,
    showCommentButton: true,
    device: {
      pc: {
        title: '',
        parameter: '',
        showNewButton: true,
        showEditButton: true,
        showDeleteButton: true,
        showCommentButton: true
      },
      mobile: {
        title: '',
        parameter: '',
        showNewButton: true,
        showEditButton: true,
        showDeleteButton: true,
        showCommentButton: true
      }
    }
  }
};

// 컴포넌트 등록 정보
export const componentConfig = {
  type: COMPONENT_TYPE,
  icon: COMPONENT_ICON,
  label: COMPONENT_LABEL,
  description: COMPONENT_DESCRIPTION,
  component: BoardComponent,
  editor: BoardEditor,
  properties: BoardProperties,
  defaultProps
};

// 모든 관련 모듈 내보내기
export {
  BoardComponent,
  BoardEditor,
  BoardProperties,
  adaptBoardProps,
  adaptBoardEditorProps
};

// 타입 내보내기
export type {
  BoardProps,
  BoardEditorProps,
  BoardData
};

// 스킨 시스템을 사용하는 래퍼 컴포넌트
const Board = (props: any) => {
  const selectedSkin = props.componentProps?.skin || props.skin || 'basic';
  
  const componentData = {
    id: props.id || `${COMPONENT_TYPES.BOARD}-${Date.now()}`,
    type: COMPONENT_TYPES.BOARD,
    props: props,
    componentProps: props.componentProps || {},
    data: props.data || {},
    content: props.content || {},
    style: props.style || {},
    isPreview: props.isPreview,
    fetchBoardsOnce: props.fetchBoardsOnce,
    fetchPostsOnce: props.fetchPostsOnce,
    pathVariables: props.pathVariables || {}
  };
  
  const mode = props.isEditor ? 'editor' : props.isPreview ? 'preview' : 'production';
  
  const editorProps = props.isEditor ? {
    isSelected: props.isSelected || false,
    onSelect: props.onSelect || (() => {}),
    onEdit: props.onEdit || (() => {}),
    onDelete: props.onDelete || (() => {}),
    dragHandleProps: props.dragHandleProps
  } : undefined;
  
  return (
    <ComponentSkinWrapper
      component={BoardSkinnable}
      componentData={componentData}
      skinId={selectedSkin}
      mode={mode}
      editorProps={editorProps}
    />
  );
};

// 스킨 컴포넌트 등록
registerComponent({
  type: COMPONENT_TYPES.BOARD,
  name: BoardSkinnable.name,
  component: Board,
  editorComponent: BoardEditor,
  properties: BoardProperties,
  defaultProps: BoardSkinnable.defaultProps,
  category: BoardSkinnable.category,
  icon: BoardSkinnable.icon,
  skinnable: BoardSkinnable
});

// 레거시 컴포넌트 등록 (하위 호환성)
registerComponent({
  type: COMPONENT_TYPE,
  name: COMPONENT_LABEL,
  component: BoardComponent,
  editorComponent: BoardEditor,
  properties: BoardProperties,
  defaultProps,
  category: 'content',
  icon: COMPONENT_ICON
});

// 기본 내보내기는 Board 스킨 래퍼 컴포넌트
export default Board; 